/**
 * en-GB messages added by Phase 3 task W21 (docs/PHASE-3-PLAN.md). Only that task edits this file.
 * Flat keys, `{name}` placeholders, merged into enGB by ./index.ts.
 * Public pages for visitors on their own phone: short sentences, plain words.
 */
export const w21EnGB = {
    // ---------- Frame and common ----------
    'w21.footer': 'Your details are used only to sign you in and keep this site safe.',
    'w21.logoAlt': '{name} logo',
    'w21.loading': 'Loading',
    'w21.rateLimited': 'Too many tries. Please wait a few minutes and try again.',
    'w21.next': 'Next',
    'w21.back': 'Back',
    'w21.skip': 'Skip this step',
    'w21.stepOf': 'Step {n} of {total}',
    // ---------- Start ----------
    'w21.start.title': 'Sign in at {site}',
    'w21.start.mode.in': '{point}: sign in here.',
    'w21.start.mode.out': '{point}: sign out here.',
    'w21.start.mode.both': '{point}: sign in or out here.',
    'w21.start.privacy': 'We will ask for your name and a few other details. They are kept only as long as the site needs them.',
    'w21.start.button': 'Start',
    'w21.start.buttonLocation': 'Share my location and start',
    'w21.start.locationWhy': 'This site checks that you are close by. Your location is used once to check that, and it is not stored.',
    'w21.start.locationDenied': 'We could not use your location. Allow location for this page in your browser settings and try again, or ask at reception.',
    'w21.start.locationFailed': 'We could not find your location. Move to a place with a better signal and try again, or ask at reception.',
    'w21.start.ended': 'Your session has ended. Start again to carry on.',
    // ---------- Sign in ----------
    'w21.signIn.title': 'Sign in at {site}',
    'w21.signIn.button': 'Sign in',
    'w21.noGroups': 'Sign-in on your own phone is not set up for any visitor type here. Please see reception.',
    'w21.group.title': 'Who are you?',
    'w21.details.title': 'Your details',
    'w21.details.name': 'Your full name',
    'w21.details.company': 'Company (if any)',
    'w21.details.email': 'Email address',
    'w21.details.emailHint': 'Optional. Used only if the site needs to contact you.',
    'w21.details.nameRequired': 'Please enter your name.',
    'w21.details.fullNameRequired': 'Please enter your first name and surname.',
    'w21.details.emailInvalid': 'That email address does not look right.',
    'w21.host.label': 'Who are you here to see?',
    'w21.host.hint': 'Type at least 2 letters of their name.',
    'w21.host.results': 'People found',
    'w21.host.none': 'Nobody found with that name. You can leave this blank and tell reception.',
    'w21.host.chosen': 'Visiting: {name}',
    'w21.host.change': 'Change',
    'w21.remember.label': 'Remember me on this phone',
    'w21.remember.hint': 'Saves your name, company and email on this phone so next time is quicker.',
    'w21.remember.saved': 'This phone will remember your details for next time.',
    'w21.forget.button': 'Forget me on this phone',
    // ---------- Steps ----------
    'w21.signature.useTyped': 'I cannot draw here. Let me type my name instead.',
    'w21.signature.typedLabel': '{label}: type your full name',
    'w21.signature.typedHint': 'Typing your name counts as your signature.',
    'w21.notices.agree': 'I have read this and I agree.',
    'w21.notices.signHere': 'Sign here',
    'w21.notices.needAgree': 'Please tick to agree to "{title}".',
    'w21.notices.needSign': 'Please sign "{title}".',
    'w21.photo.help': 'A photo helps reception know who you are. You can skip this.',
    'w21.photo.helpRequired': 'This site needs a photo of you. Tap the button to take one with your phone.',
    'w21.photo.take': 'Take a photo',
    'w21.photo.retake': 'Take another photo',
    'w21.photo.working': 'Sending your photo',
    'w21.photo.done': 'Photo added.',
    'w21.photo.notImage': 'That file is not a picture.',
    'w21.photo.failed': 'We could not use that picture. Please try again.',
    // ---------- Induction (contactless and pre-arrival) ----------
    'w21.induction.title': 'Induction',
    'w21.induction.help': 'Please watch or read this and answer the questions before you arrive.',
    'w21.induction.openDocument': 'Open the document',
    'w21.induction.haveRead': 'I have read it.',
    'w21.induction.watchFirst': 'Watch the video for at least {seconds} seconds to unlock the questions.',
    'w21.induction.quizTitle': 'Questions (you need {pass}% to pass)',
    'w21.induction.tryAgain': 'You scored {score}%, with {wrong} wrong. Please try again.',
    'w21.induction.tryAgainScore': 'You scored {score}%. Please try again.',
    'w21.induction.complete': 'I have completed this',
    'w21.induction.passed': 'Passed with {score}%.',
    'w21.induction.unavailable': 'The induction could not be loaded. Reception will help you on arrival.',
    // ---------- Thanks, sign out ----------
    'w21.thanks.title': 'You are signed in',
    'w21.thanks.alreadyIn': 'You are already signed in on this phone.',
    'w21.thanks.signOutHint': 'When you leave, open this page again and sign out.',
    'w21.thanks.outTitle': 'You are signed out',
    'w21.thanks.outDefault': 'Thank you for visiting. Safe journey.',
    'w21.signOut.open': 'Sign out now',
    'w21.signOut.title': 'Sign out',
    'w21.signOut.since': 'You signed in at {time} as {group}.',
    'w21.signOut.button': 'Sign out',
    'w21.noOpenVisit': 'There is no open visit on this phone to sign out. Please see reception.',
    'w21.refused.title': 'Please see reception',
    'w21.refused': 'Please see reception. They will help you from here.',
    'w21.refused.retry': 'Go back and try again',
    // ---------- Evacuation ----------
    'w21.evac.title': 'Evacuation in progress',
    'w21.evac.leave': 'Leave the building now by the nearest safe exit. Do not use the lifts.',
    'w21.evac.drill': 'This is a fire drill. Leave the building now by the nearest safe exit.',
    'w21.evac.points': 'Go to an assembly point',
    'w21.evac.imSafe': "I'm safe",
    'w21.evac.imSafeHelp': 'Tap once you have reached a safe place. The fire marshals will see it.',
    'w21.evac.markedSafe': 'You are marked as safe. Thank you.',
    'w21.evac.notSignedIn': 'Please follow the fire marshals to the assembly point.',
    // ---------- Pre-arrival: documents ----------
    'w21.docs.title': 'Your documents',
    'w21.docs.help': 'Upload what the site needs from you before you arrive. Reception will check them.',
    'w21.docs.state.valid': 'Up to date',
    'w21.docs.state.expiring': 'Expires soon',
    'w21.docs.state.expired': 'Expired',
    'w21.docs.state.missing': 'Needed',
    'w21.docs.state.pending_review': 'Waiting for a check',
    'w21.docs.state.rejected': 'Not accepted',
    'w21.docs.rejectedHelp': 'The last one was not accepted. Please upload a new one.',
    'w21.docs.file': 'Document (PDF or photo)',
    'w21.docs.fileHint': 'Up to 20 MB.',
    'w21.docs.reference': 'Reference or certificate number (optional)',
    'w21.docs.issueDate': 'Issue date',
    'w21.docs.expiryDate': 'Expiry date',
    'w21.docs.save': 'Send this document',
    'w21.docs.needFile': 'Please add the document first.',
    'w21.docs.needExpiry': 'Please enter the expiry date.',
    'w21.docs.needIssue': 'Please enter the issue date.',
    'w21.docs.ok': 'Nothing more needed.',
    'w21.docs.waiting': 'Sent. Waiting for the site to check it.',
    // ---------- Pre-arrival: permits ----------
    'w21.permits.title': 'Your permits',
    'w21.permits.help': 'Read each permit and accept it before you start work.',
    'w21.permits.heading': '{type} ({number})',
    'w21.permits.status.requested': 'Requested',
    'w21.permits.status.issued': 'Ready to accept',
    'w21.permits.status.active': 'In use',
    'w21.permits.status.suspended': 'Paused',
    'w21.permits.status.closed': 'Closed',
    'w21.permits.status.cancelled': 'Cancelled',
    'w21.permits.status.expired': 'Expired',
    'w21.permits.acceptedBadge': 'Accepted',
    'w21.permits.area': 'Where',
    'w21.permits.when': 'When',
    'w21.permits.fromTo': 'from {from} to {to}',
    'w21.permits.conditions': 'Conditions',
    'w21.permits.precautions': 'Precautions',
    'w21.permits.acceptHelp': 'By signing you confirm you have read the conditions and precautions and will follow them.',
    'w21.permits.name': 'Your full name',
    'w21.permits.signHere': 'Sign here',
    'w21.permits.accept': 'Accept this permit',
    'w21.permits.needSign': 'Please sign, or type your name, to accept.',
    'w21.permits.needName': 'Please enter your full name.',
};
//# sourceMappingURL=w21.js.map